import { createFileRoute } from "@tanstack/react-router";
import { GrowthPlanSections } from "@/components/GrowthPlanSections";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do dental practices in Colorado get more new patients online?",
    a: "Colorado dental practices win new patients through a combination of local SEO, Google Maps optimization, and a fast, conversion-focused website with clear new-patient and insurance information above the fold. We tune Google Business Profiles around how Colorado families actually search — and we add FAQ + Dentist schema so you also show up in AI-generated answers.",
  },
  {
    q: "How long does dental SEO take to work in Colorado?",
    a: "Most Colorado dental practices see meaningful Map Pack movement in 60–120 days and stronger organic new-patient bookings within 4–6 months. Recency of reviews matters as much as quantity — we build a sustainable monthly review system, not a one-time spike.",
  },
  {
    q: "What's the difference between dental marketing and general digital marketing?",
    a: "Dental marketing requires HIPAA-aware content choices, clear insurance/financing communication, and FAQ structures that match how parents and patients actually search ('pediatric dentist near me', 'dentist that takes Delta Dental Colorado'). We build these into every page — not generic small-business templates.",
  },
  {
    q: "Do you handle Google Business Profile and reviews for dental offices?",
    a: "Yes — full GBP management is included in our Growth Marketing package. We post weekly, respond to every review, add geo-tagged photos, and build a structured review-request workflow tied to appointment completion in your practice management software.",
  },
  {
    q: "How much does dental marketing cost in Colorado?",
    a: "Our Growth Marketing package is $1,650/month and includes SEO, Google Maps, GEO AI, and social — the full stack most Colorado practices need. Websites start at $1,850 one-time, owned outright. We publish prices because dentists deserve a straight answer, not a sales pitch.",
  },
];

export const Route = createFileRoute("/dental-marketing-colorado")({
  head: () =>
    industryMarketingHead("Dental", "dental practice", "dental-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Dental"
      industryLower="dental practice"
      slug="dental-marketing-colorado"
      subheadline="We help Colorado dental practices grow new-patient bookings with local SEO, fast websites, Google Maps, and AI search visibility — without resorting to high-pressure marketing contracts."
      painPoints={[
        "New-patient bookings have plateaued for years despite a great team",
        "Beautiful but slow website that buries the appointment form",
        "Outranked in Google Maps by national franchise dental brands",
      ]}
      serviceDescriptions={{
        seo: "Rank for 'family dentist', 'pediatric dentist', and 'cosmetic dentist' in every Colorado market you serve.",
        googleMaps: "Top-3 Map Pack visibility for the searches new patients actually run.",
        webDesign: "Fast, mobile-first dental websites with insurance, new-patient specials, and online booking up top.",
        ppc: "Profitable Google Ads tuned to your highest-value services and target neighborhoods.",
        geoAi: "Get cited when Colorado families ask ChatGPT or Gemini 'best dentist near me.'",
        socialMedia: "Local trust-building content that turns Colorado neighbors into long-term patients.",
      }}
      testimonial={{
        quote:
          "We had multiple new patients tell us they booked because of the new site. The doctors actually noticed it on the schedule.",
        role: "Practice Manager, Family Dental Practice",
        city: "Monument",
      }}
      faqs={FAQS}
      extraSections={<GrowthPlanSections
          quoteIndustry="Dental"
          eyebrow="Patient acquisition"
          tipsHeading="How Colorado dental practices win new patients"
          tips={[
            { title: "Insurance up front", body: "List the plans you take near the top of every page. It is the first thing new patients check." },
            { title: "One page per treatment", body: "Implants, Invisalign, emergency, cosmetic and family dentistry each need their own page to show up in search." },
            { title: "Book in two taps", body: "A visible call button and short booking form on phones turns searches into appointments." },
            { title: "Reviews after every visit", body: "A steady flow of fresh Google reviews keeps you in the top three on Google Maps." },
            { title: "Real photos of your team", body: "Patients choose a dentist they feel they know. Skip stock photos." },
            { title: "Clear answers for AI search", body: "Short, honest answers to common questions help ChatGPT and Google name your practice." },
          ]}
          planHeading="Your 90-day local SEO plan for a Colorado dental practice"
          plan={[
            { label: "Days 1–30", title: "Fix the foundation", items: ["Google Business Profile categories, hours, photos and services", "Fast, mobile-friendly site with insurance and booking up top", "Name, address and phone matched across directories"] },
            { label: "Days 31–60", title: "Build treatment and town pages", items: ["Pages for implants, Invisalign, emergency and family care", "Pages for the nearby towns you draw patients from", "Patient questions answered in plain words"] },
            { label: "Days 61–90", title: "Grow trust and visibility", items: ["Review requests tied to completed visits", "Weekly Google Business Profile posts", "Track calls and bookings, then double down on what works"] },
          ]}
          caseStudySlugs={["dental-woodland-park"]}
          links={[
            { to: "/learn/tips/dental-marketing-tips-colorado", label: "Dental marketing tips for Colorado" },
            { to: "/dental-web-design-castle-rock", label: "Dental web design in Castle Rock" },
            { to: "/web-design-castle-rock-colorado", label: "Castle Rock web design company" },
            { to: "/pricing", label: "Pricing" },
          ]}
        />}
      industrySpokes={[
        {
          "to": "/dental-seo-colorado",
          "label": "Dental SEO in Colorado",
          "description": "Rank for new-patient searches across Colorado — implants, ortho, and family dental."
        },
        {
          "to": "/dental-google-maps-colorado",
          "label": "Dental Google Maps in Colorado",
          "description": "Map Pack ranking and GBP optimization for Colorado dental practices."
        }
      ]}
      cityHubLinks={[
        {
          "to": "/colorado-springs",
          "label": "Dental marketing in Colorado Springs",
          "description": "Springs new-patient dental SEO and GBP from our local HQ."
        },
        {
          "to": "/denver-digital-marketing",
          "label": "Dental marketing in Denver",
          "description": "Compete in Denver's saturated dental market with practice-area SEO."
        },
        {
          "to": "/fort-collins-digital-marketing",
          "label": "Dental marketing in Fort Collins",
          "description": "Northern Colorado dental SEO and new-patient lead generation."
        }
      ]}
    />
  ),
});
